import { CBCore } from "cbcore-ts"
import {
    CALL,
    IF,
    IS,
    LooseObject,
    nil,
    NO,
    UIColor,
    UICore,
    UIRoute,
    UITextView,
    UIView,
    UIViewBroadcastEvent,
    YES
} from "uicore-ts"
import { CBColor } from "./Custom components/CBColor"
import { RHBarButton } from "./Custom components/RH/RHBarButton"
import { LoginViewController } from "./LoginViewController"
import { ProcurerMainViewController } from "./ProcurerMainViewController"


export class TopBarView extends UIView {
    
    // Define containers first
    topSectionView = new UIView().configuredWithObject({
        backgroundColor: new UIColor("#0065b2")
    }).addedAsSubviewToView(this)
    
    bottomBarView = new UIView().configuredWithObject({
        backgroundColor: UIColor.whiteColor
    }).addedAsSubviewToView(this)
    
    titleLabel = new UITextView("TopBarTitleLabel")
        .configuredWithObject({
            
            setText: CALL("topBarTitle", "TestPage"),
            textSuffix: (CBCore.sharedInstance.userProfile?.contactInformation?.name ?? ""),
            textColor: UIColor.whiteColor,
            fontSize: 25,
            userInteractionEnabled: YES,
            nativeSelectionEnabled: NO,
            style: { cursor: "pointer" },
            controlEventTargetAccumulator: {
                PointerUpInside: (sender, event) => {
                    UIRoute.currentRoute.routeByRemovingComponentsOtherThanOnesNamed(
                        ["settings"]).apply()
                }
            }
            
        }).addedAsSubviewToView(this.topSectionView) // Parented to topSectionView
    
    userHomeButton = new RHBarButton().configuredWithObject({
        titleLabel: { text: "Kasutaja avavaade" },
        controlEventTargetAccumulator: {
            // @ts-ignore
            EnterDown: {
                PointerUpInside: (sender, event) => {
                    
                    UIRoute.currentRoute.routeByRemovingComponentsOtherThanOnesNamed(
                            ["settings"])
                        .routeWithViewControllerComponent(ProcurerMainViewController, nil)
                        .apply()
                    
                }
            }
        }
    }).addedAsSubviewToView(this.bottomBarView)
    
    loginButton = new RHBarButton().configuredWithObject({
        titleLabel: { text: "Logi sisse" },
        variant: "outlineOnDarkBackground",
        controlEventTargetAccumulator: {
            // @ts-ignore
            EnterDown: {
                PointerUpInside: (sender, event) => {
                    
                    UIRoute.currentRoute.routeByRemovingComponentsOtherThanOnesNamed(
                            ["settings"])
                        .routeWithViewControllerComponent(LoginViewController, nil)
                        .apply()
                    
                }
            }
        }
    })//.addedAsSubviewToView(this.topSectionView) // Parented to topSectionView
    
    logoutButton = new RHBarButton().configuredWithObject({
        titleLabel: { text: "Logi välja" },
        variant: "outlineOnDarkBackground",
        controlEventTargetAccumulator: {
            // @ts-ignore
            EnterDown: {
                PointerUpInside: async (sender, event) => {
                    
                    const result = await fetch("/auth/logout", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" }
                    })
                    
                    CBCore.sharedInstance.userProfile = undefined
                    
                    UIRoute.currentRoute.routeByRemovingComponentsOtherThanOnesNamed(
                            ["settings"])
                        .apply()
                    
                }
            }
        }
    })//.addedAsSubviewToView(this.topSectionView) // Parented to topSectionView
    
    
    constructor(elementID: string | undefined, element?: HTMLElement & LooseObject) {
        
        super(elementID, element)
        
        
        this.addStyleClass("TopBarView");
        
        // Grouped CSS in configureWithObject per Strict Mode
        (this as TopBarView).configureWithObject({
            setBorder: [nil, 0, CBColor.primaryContentColor],
            style: {
                borderBottomWidth: "0px",
                fontSize: "15pt",
                zIndex: "10",
                boxShadow: "0px 4px 10px rgba(0,0,0,0.2)"
            }
        })
        
        
    }
    
    
    override didReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        
        super.didReceiveBroadcastEvent(event)
        
        if (event.name == UICore.broadcastEventName.RouteDidChange) {
            this.selectCorrectButtons()
        }
        
        if (
            event.name == CBCore.broadcastEventName.userDidLogIn ||
            event.name == CBCore.broadcastEventName.userDidLogOut
        ) {
           
            this.titleLabel.textSuffix = CBCore.sharedInstance.userProfile?.contactInformation?.name ?? ""
            // Update visibility on login state change
            this.selectCorrectButtons()
            
            this.setNeedsLayoutUpToRootView()
            
        }
        
    }
    
    override didMoveToSuperview(superview: UIView) {
        super.didMoveToSuperview(superview)
        this.selectCorrectButtons()
    }
    
    private selectCorrectButtons() {
        const route = UIRoute.currentRoute
        
        // Select correct button based on which view controller component is present in the route
        this.userHomeButton.selected = IS(route.componentWithViewController(ProcurerMainViewController))
        this.loginButton.selected = IS(route.componentWithViewController(LoginViewController))
        
        // Show bottom bar only if logged in
        this.bottomBarView.hidden = !CBCore.sharedInstance.isUserLoggedIn
        
        this.setNeedsLayout()
        
    }
    
    
    override layoutSubviews() {
        
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.bounds
        const isLoggedIn = CBCore.sharedInstance.isUserLoggedIn
        
        // Top section height is fixed, or fills view if logged out
        const topHeight: number = IF(isLoggedIn)(() => 65).ELSE(() => bounds.height)
        
        this.topSectionView.frame = bounds.rectangleWithHeight(topHeight)
        
        // Layout Title (Centered in Top Section)
        this.titleLabel.centerInContainer()
        
        // Layout Login/Logout (Centered in Top Section)
        const logButton = IF(isLoggedIn)(() =>
            this.logoutButton
        ).ELSE(() =>
            this.loginButton
        )
        
        this.loginButton.hidden = (logButton == this.logoutButton)
        this.logoutButton.hidden = (logButton == this.loginButton)
        
        logButton.frame = this.topSectionView.bounds.rectangleWithWidth(150, 1)
            .rectangleWithInset(padding * 0.5)
        
        
        // Bottom section
        if (isLoggedIn) {
            // Layout User Home Button (Centered in Bottom Section)
            const bottomHeight = bounds.height - topHeight
            this.bottomBarView.frame = bounds.rectangleWithHeight(bottomHeight, 1)
            
            this.userHomeButton.frame = this.bottomBarView.bounds.rectangleWithWidth(200 + padding * 0.5)
                .rectangleWithInset(padding * 0.25)
        }
        else {
            // Ensure frame is zeroed out or valid if hidden, though hidden handles display
            this.bottomBarView.frame = bounds.rectangleWithHeight(0, 1)
        }
        
    }
    
    override intrinsicContentHeight(constrainingWidth?: number): number {
        // Return full height if logged in, otherwise just top section height
        if (CBCore.sharedInstance.isUserLoggedIn) {
            return 65 + 50
        }
        return 65
    }
    
    
}
